import { apiAuth, apiResponseType } from ".";

/** Aba "Resultado Clientes" (menu Vendas > Resultado Clientes) */

export interface ClientResultsParams {
  date_start?: string;
  date_end?: string;
  lottery_id?: string[];
  contest_names?: string;
  seller_id?: string[];
  unity_id?: string[];
  search?: string;
  page?: number;
  per_page?: number;
}

export type ClientResultContestStatus = "investigated" | "pending";

export type ClientResultProductStatus =
  | "awarded"
  | "not_awarded"
  | "pending"
  | "not_generated";

export interface ClientResultClient {
  id: number;
  uuid: string;
  name: string;
  document: string | null;
  phone: string | null;
  total_balance: number;
}

export interface ClientResultProduct {
  contest_product_id: number;
  product_name: string;
  classification_name: string | null;
  description: string | null;
  quotas: number;
  investigation_status: ClientResultProductStatus;
  prize_per_quota: number | null;
  prize: number | null;
  receive_by_cashier: boolean;
}

export interface ClientResultContest {
  contest_id: number;
  contest_name: string;
  lottery_id: number;
  lottery_name: string;
  lottery_color: string | null;
  validation_date: string;
  status: ClientResultContestStatus;
  quotas: number;
  products_count: number;
  /** Prêmio Total: soma informativa de todas as premiações, inclusive as pagas só na Caixa */
  total_prize: number | null;
  /** Recebe na lotérica e entra no saldo do cliente (fora o que é pago só na Caixa) */
  total_wallet_prize: number | null;
  /** Pagamento só na Caixa: não entra no saldo do cliente */
  total_cashier_prize: number | null;
  has_cashier_receipt: boolean;
  products?: ClientResultProduct[];
}

export interface ClientResultFilters {
  date_start: string;
  date_end: string;
  lottery_id: number[];
  contest_names: string[];
  seller_id: number[];
  unity_id: number[];
  search: string | null;
  scope: "seller" | "all";
}

export interface ClientResultRow {
  client: ClientResultClient;
  contests: ClientResultContest[];
  total_prize: number;
  has_pending: boolean;
}

export interface ClientResultsResponse {
  data: ClientResultRow[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  totals: {
    total_clients: number;
    total_prize: number;
    investigated_contests_count: number;
    pending_contests_count: number;
  };
  filters: ClientResultFilters;
}

export interface ClientResultDetailResponse {
  client: ClientResultClient;
  contests: ClientResultContest[];
  summary: {
    total_prize: number;
    total_wallet_prize: number;
    total_cashier_prize: number;
    contests_count: number;
    investigated_contests_count: number;
    pending_contests_count: number;
    products_count: number;
    quotas_count: number;
  };
  filters: ClientResultFilters;
  legal_notice: string;
}

export const buildClientResultsQuery = (params?: ClientResultsParams) => {
  const query = new URLSearchParams();

  if (params?.date_start) query.append("date_start", params.date_start);
  if (params?.date_end) query.append("date_end", params.date_end);
  params?.lottery_id?.forEach((id) => query.append("lottery_id[]", id));
  params?.seller_id?.forEach((id) => query.append("seller_id[]", id));
  params?.unity_id?.forEach((id) => query.append("unity_id[]", id));
  if (params?.contest_names?.trim()) query.append("contest_names", params.contest_names.trim());
  if (params?.search?.trim()) query.append("search", params.search.trim());
  if (params?.page) query.append("page", String(params.page));
  if (params?.per_page) query.append("per_page", String(params.per_page));

  return query.toString();
};

export const getClientResults = (params?: ClientResultsParams): apiResponseType =>
  apiAuth.get(`client-results?${buildClientResultsQuery(params)}`) as apiResponseType;

export const getClientResultDetail = (
  clientUuid: string,
  params?: ClientResultsParams
): apiResponseType =>
  apiAuth.get(`client-results/${clientUuid}?${buildClientResultsQuery(params)}`) as apiResponseType;

/** Demonstrativo em PDF. Em erro o interceptor já mostra o toast e não devolve arquivo. */
export const downloadClientResultPdf = (
  clientUuid: string,
  params?: ClientResultsParams
): Promise<Blob | null> =>
  apiAuth
    .get(`client-results/${clientUuid}/pdf?${buildClientResultsQuery(params)}`, {
      responseType: "blob",
    })
    .then((response: any) => (response?.data instanceof Blob ? response.data : null));
